// InlineEdit.tsx — hover-to-edit inline text + editable hyperlinks.
// <E path="bio.name">Lior</E>     → click to edit inline.
// <EL path="bio.cta" href="#">CTA</EL> → click → dropdown: Change text / Change link.
// Edits persist via EditProvider (backed by tweaks.contentEdits).

import React from 'react';
import { createPortal } from 'react-dom';
import './template.css';
import {
  sanitizePublicCardTitle,
} from '../../../Utils/media/feedCardTitle';
import { sanitizeBioDescription } from '../StayUpToDate/StayUpToDateComponents/EditTemplates/EditBio';

/** Catalog product title paths — never resurface LTK/handle chrome from contentEdits. */
function isProductNameEditPath(path: string): boolean {
  return /^product\.[^.]+\.name$/.test(path);
}

function cleanProductNameEditValue(
  raw: string,
  fallback: string,
): string {
  const cleaned = sanitizePublicCardTitle(raw, {
    type: 'PRODUCT',
    product_name: raw,
  });
  if (cleaned) return cleaned;
  // Stored value was junk (Follow / on LTK / username) — fall back to children.
  return sanitizePublicCardTitle(fallback, {
    type: 'PRODUCT',
    product_name: fallback,
  });
}

/** Opens template CTA / button hrefs in a new tab (or same window for mailto/tel). */
export function openTemplateButtonHref(rawUrl: string): boolean {
  const u = rawUrl.trim();
  if (!u || u === '#') return false;
  try {
    if (u.startsWith('mailto:') || u.startsWith('tel:')) {
      window.location.href = u;
      return true;
    }
    const abs =
      u.startsWith('http://') ||
      u.startsWith('https://') ||
      u.startsWith('//') ||
      u.startsWith('/') ||
      u.startsWith('./') ||
      u.startsWith('../')
        ? new URL(u, window.location.origin).href
        : new URL(`https://${u.replace(/^\/+/, '')}`).href;
    window.open(abs, '_blank', 'noopener,noreferrer');
    return true;
  } catch {
    return false;
  }
}

function isLinkableTemplateHref(raw?: string): boolean {
  const u = (raw ?? '').trim();
  return Boolean(u && u !== '#');
}

/** Home is the required base nav label — never persist an empty string for these paths. */
function isRequiredNonEmptyLabelPath(path: string): boolean {
  return path === 'nav.home' || path === 'footer.navLabel.home';
}

function coerceRequiredLabel(path: string, value: string, fallback: string): string {
  if (!isRequiredNonEmptyLabelPath(path)) return value;
  const t = value.trim();
  if (t) return t;
  return fallback.trim() || 'Home';
}

// ── CSS (injected once) ────────────────────────────────────────────────────
const INLINE_EDIT_CSS = `
  /*
   * Nav keeps its own type from template.css (size, tracking, uppercase).
   * Inheriting here was forcing Title Case + wrong size in the dashboard preview.
   */
  [data-om-edit]:not(.mte-tpl-btn):not(.mte-tpl-nav-link):not(.mte-tpl-nav-more-btn):not(.mte-tpl-nav-more-item):not(.mte-tpl-nav-search-muted):not(.mte-tpl-nav-full-link):not(.mte-tpl-nav-drawer-link):not(.mte-tpl-nav-min-item):not(.mte-tpl-nav-wordmark):not(.mte-tpl-nav-wordmark-txt):not(.mte-schema-text-title):not(.mte-tpl-prod-name-edit) {
    cursor: text;
    transition: background-color 0.12s ease, box-shadow 0.12s ease;
    border-radius: 2px;
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
    box-sizing: border-box;
    vertical-align: baseline;
    font: inherit;
    letter-spacing: inherit;
    text-transform: inherit;
    font-variant: inherit;
    font-feature-settings: inherit;
    color: inherit;
    -webkit-text-fill-color: inherit;
  }
  /* Product catalog titles must keep -webkit-line-clamp; baseline + hover
   * chrome on the edit host was blowing out masonry on the dashboard only. */
  [data-om-edit].mte-tpl-prod-name-edit {
    cursor: text;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
    overflow-wrap: anywhere;
    word-break: break-word;
    max-width: 100%;
    vertical-align: unset;
    box-sizing: border-box;
    font: inherit;
    color: inherit;
    -webkit-text-fill-color: inherit;
  }
  [data-om-edit].mte-tpl-prod-name-edit:hover {
    background-color: rgba(110,150,220,.14);
    box-shadow: inset 0 -1px 0 rgba(110,150,220,.55);
  }
  [data-om-edit]:not(.mte-tpl-btn):not(.mte-tpl-nav-link):not(.mte-tpl-nav-more-btn):not(.mte-tpl-nav-more-item):not(.mte-tpl-nav-search-muted):not(.mte-tpl-nav-full-link):not(.mte-tpl-nav-drawer-link):not(.mte-tpl-nav-min-item):not(.mte-tpl-nav-wordmark):not(.mte-tpl-nav-wordmark-txt):hover {
    background-color: rgba(110,150,220,.14);
    box-shadow: inset 0 -1px 0 rgba(110,150,220,.55);
  }
  /* Nav: editable cursor only — typography comes from .mte-tpl-nav-* rules. */
  [data-om-edit].mte-tpl-nav-link,
  [data-om-edit].mte-tpl-nav-more-btn,
  [data-om-edit].mte-tpl-nav-more-item,
  [data-om-edit].mte-tpl-nav-search-muted,
  [data-om-edit].mte-tpl-nav-full-link,
  [data-om-edit].mte-tpl-nav-drawer-link,
  [data-om-edit].mte-tpl-nav-min-item,
  [data-om-edit].mte-tpl-nav-wordmark,
  [data-om-edit].mte-tpl-nav-wordmark-txt {
    cursor: text;
    transition: background-color 0.12s ease, box-shadow 0.12s ease;
    box-sizing: border-box;
  }
  /* Keep the active nav underline a straight line — radius turns border-bottom
     into a tray/bracket that does not match the public site. */
  [data-om-edit].mte-tpl-nav-link,
  [data-om-edit].mte-tpl-nav-more-btn {
    border-radius: 0;
  }
  [data-om-edit].mte-tpl-nav-more-item,
  [data-om-edit].mte-tpl-nav-search-muted,
  [data-om-edit].mte-tpl-nav-full-link,
  [data-om-edit].mte-tpl-nav-drawer-link,
  [data-om-edit].mte-tpl-nav-min-item,
  [data-om-edit].mte-tpl-nav-wordmark,
  [data-om-edit].mte-tpl-nav-wordmark-txt {
    border-radius: 2px;
  }
  [data-om-edit].mte-tpl-nav-link:hover,
  [data-om-edit].mte-tpl-nav-more-btn:hover,
  [data-om-edit].mte-tpl-nav-more-item:hover,
  [data-om-edit].mte-tpl-nav-search-muted:hover,
  [data-om-edit].mte-tpl-nav-full-link:hover,
  [data-om-edit].mte-tpl-nav-drawer-link:hover,
  [data-om-edit].mte-tpl-nav-min-item:hover,
  [data-om-edit].mte-tpl-nav-wordmark:hover,
  [data-om-edit].mte-tpl-nav-wordmark-txt:hover {
    background-color: transparent;
    box-shadow: none;
    text-decoration: none;
  }
  :is(a, button, span).mte-tpl-btn[data-om-edit-link],
  :is(a, button, span).mte-tpl-btn[data-om-edit] {
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 14px 22px;
    font: 12px/1 var(--tpl-ui-font, "Finder", system-ui, sans-serif);
    letter-spacing: 0.16em;
    text-transform: uppercase;
    text-decoration: none;
    white-space: nowrap;
    box-sizing: border-box;
    border-radius: var(--tpl-radius, 8px);
    -webkit-text-fill-color: currentColor;
    box-decoration-break: initial;
    vertical-align: middle;
  }
  .mte-tpl-btn [data-om-edit] {
    font: inherit;
    letter-spacing: inherit;
    text-transform: inherit;
    color: inherit;
    -webkit-text-fill-color: currentColor;
    background: transparent;
    box-shadow: none;
    outline: none;
    vertical-align: baseline;
    cursor: inherit;
    border-radius: 0;
  }
  .mte-tpl-btn [data-om-edit]:hover {
    background: transparent;
    box-shadow: none;
  }
  :is(a, button, span).mte-tpl-btn[data-om-edit-link]:hover,
  :is(a, button, span).mte-tpl-btn[data-om-edit]:hover {
    box-shadow: none;
    outline: 2px solid rgba(110, 150, 220, 0.55);
    outline-offset: 2px;
  }
  :is(a, button, span).mte-tpl-btn--primary[data-om-edit-link]:hover,
  :is(a, button, span).mte-tpl-btn--primary[data-om-edit]:hover {
    background: var(--tpl-ink);
    color: var(--tpl-bg);
  }
  :is(a, button, span).mte-tpl-btn--ghost[data-om-edit-link]:hover,
  :is(a, button, span).mte-tpl-btn--ghost[data-om-edit]:hover {
    background: transparent;
    /* Keep resting color — forcing --tpl-ink hides labels on accent shells. */
    color: inherit;
    -webkit-text-fill-color: currentColor;
  }
  :is(a, button, span).mte-tpl-btn--on-image[data-om-edit-link]:hover,
  :is(a, button, span).mte-tpl-btn--on-image[data-om-edit]:hover {
    background: #fff;
    color: #000;
  }
  :is(a, button, span).mte-tpl-btn--invert-accent[data-om-edit-link]:hover,
  :is(a, button, span).mte-tpl-btn--invert-accent[data-om-edit]:hover {
    background: var(--tpl-accent-ink);
    color: var(--tpl-accent);
  }
  [data-om-edit]:not(.mte-tpl-btn)[data-editing="true"] {
    background-color: rgba(110,150,220,.18);
    box-shadow: none;
    cursor: text;
    outline: 2px solid #6BA8FF;
    outline-offset: 2px;
  }
  :is(a, button, span).mte-tpl-btn[data-om-edit-link][data-editing="true"],
  :is(a, button, span).mte-tpl-btn[data-om-edit][data-editing="true"] {
    box-shadow: none;
    cursor: text;
    outline: 2px solid #6BA8FF;
    outline-offset: 2px;
  }
  :is(a, button, span).mte-tpl-btn--primary[data-om-edit-link][data-editing="true"],
  :is(a, button, span).mte-tpl-btn--primary[data-om-edit][data-editing="true"] {
    background: var(--tpl-ink);
    color: var(--tpl-bg);
  }
  :is(a, button, span).mte-tpl-btn--ghost[data-om-edit-link][data-editing="true"],
  :is(a, button, span).mte-tpl-btn--ghost[data-om-edit][data-editing="true"] {
    background: transparent;
    color: inherit;
    -webkit-text-fill-color: currentColor;
  }
  :is(a, button, span).mte-tpl-btn--on-image[data-om-edit-link][data-editing="true"],
  :is(a, button, span).mte-tpl-btn--on-image[data-om-edit][data-editing="true"] {
    background: #fff;
    color: #000;
  }
  :is(a, button, span).mte-tpl-btn--invert-accent[data-om-edit-link][data-editing="true"],
  :is(a, button, span).mte-tpl-btn--invert-accent[data-om-edit][data-editing="true"] {
    background: var(--tpl-accent-ink);
    color: var(--tpl-accent);
  }
  [data-om-edit][data-om-edit-empty="true"][data-editing="true"] {
    display: inline-block;
    min-width: 1ch;
    min-height: 1em;
    vertical-align: middle;
    line-height: inherit;
  }
  .mte-schema-ribbon-msg [data-om-edit] {
    vertical-align: middle;
    line-height: inherit;
  }
  /* Descriptions: keep saved \\n visible after blur / on public site */
  [data-om-multiline="true"] {
    white-space: pre-wrap;
  }
  [data-om-edit][data-om-edit-empty="true"][data-editing="true"]:empty::before {
    content: none;
  }
  [data-om-edit-link] { cursor: pointer; position: relative; }
  [data-om-edit-link]:hover { outline-color: rgba(110,150,220,.55); }
  [data-om-edit-link][data-editing="true"] { text-decoration: inherit; }
  .om-edit-pop {
    background:#fff; border:1px solid rgba(0,0,0,.12); border-radius:8px;
    box-shadow:0 10px 30px rgba(0,0,0,.12); padding:4px; display:flex; flex-direction:column;
    gap:2px; min-width:160px; font: 12px/1.2 ui-monospace, monospace; letter-spacing:0.04em; color:#29261b; }
  .om-edit-pop--portal {
    position: fixed;
    z-index: 2147483647;
    pointer-events: auto;
  }
  .om-edit-pop button { background:none; border:none; padding:8px 10px; text-align:left;
    cursor:pointer; border-radius:5px; color:inherit; font:inherit; }
  .om-edit-pop button:hover { background: rgba(0,0,0,.05); }
  .om-edit-pop input { padding:8px 10px; border:1px solid rgba(0,0,0,.15); border-radius:5px;
    font:inherit; color:inherit; outline:none; }
  .om-edit-pop input:focus { border-color:#6BA8FF; }
  .om-edit-pop .om-row { display:flex; gap:4px; }
  .om-edit-session-undo {
    position: fixed;
    z-index: 2147483646;
    width: 34px;
    height: 34px;
    border-radius: 999px;
    border: 1.5px solid #6BA8FF;
    background: #fff;
    color: #1a4a8a;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    cursor: pointer;
    pointer-events: auto;
  }
  .om-edit-session-undo:hover {
    background: #eef5ff;
  }
  .om-edit-session-undo:disabled {
    opacity: 0.4;
    cursor: default;
  }
  .om-edit-session-undo svg {
    width: 17px;
    height: 17px;
    display: block;
  }
  .om-edit-session-history {
    position: fixed;
    z-index: 2147483646;
    display: inline-flex;
    gap: 6px;
    pointer-events: auto;
  }
  .om-edit-session-history .om-edit-session-undo {
    position: static;
  }
`;

let cssInjected = false;
function injectEditStyles() {
  if (cssInjected || typeof document === 'undefined') return;
  cssInjected = true;
  const s = document.createElement('style');
  s.textContent = INLINE_EDIT_CSS;
  document.head.appendChild(s);
}

/** Nav labels are CSS-uppercase; `innerText` would save that as the real name. */
function isUppercaseNavLabel(el: HTMLElement): boolean {
  return el.classList.contains('mte-tpl-nav-link')
    || el.classList.contains('mte-tpl-nav-drawer-link')
    || el.classList.contains('mte-tpl-nav-full-link')
    || el.classList.contains('mte-tpl-nav-min-item')
    || el.classList.contains('mte-tpl-nav-more-item');
}

/** Read plain text from a contentEditable node. */
function readEditText(el: HTMLElement | null, multiline: boolean): string {
  if (!el) return '';
  const source = isUppercaseNavLabel(el) ? (el.textContent || '') : el.innerText;
  const raw = source.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  return multiline ? raw : raw.replace(/\n/g, ' ');
}

/** Write plain text back into the editable DOM, preserving multiline breaks. */
function writeEditText(
  el: HTMLElement | null,
  value: string,
  multiline: boolean,
) {
  if (!el) return;
  const next = String(value ?? '');
  if (!multiline) {
    if (el.innerText !== next) el.innerText = next;
    return;
  }
  // For multiline fields prefer block-level text writes so browser Enter/<br>
  // handling stays consistent across About, bio, blog, featured copy, etc.
  if (el.innerText !== next) el.innerText = next;
}

/** Insert a soft line break — prefer <br>, fall back to a real newline. */
function insertSoftLineBreak() {
  if (document.execCommand("insertLineBreak")) return;
  if (document.execCommand("insertText", false, "\n")) return;
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return;
  const range = sel.getRangeAt(0);
  range.deleteContents();
  const br = document.createElement("br");
  range.insertNode(br);
  range.setStartAfter(br);
  range.collapse(true);
  if (!br.nextSibling) {
    br.parentNode?.appendChild(document.createElement("br"));
  }
  sel.removeAllRanges();
  sel.addRange(range);
}

function placeCaretAtEnd(el: HTMLElement) {
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
}

// ── Per-field undo store (survives blur / Enter; resets with EditProvider) ─
// Loom requirement: undo chip must remain after commit, reappear on re-click,
// and walk back to the base value from when the template was opened.
type FieldUndoEntry = {
  /** Value when this path was first seen in the editor session. */
  base: string;
  /** Snapshots older → newer; top is previous text before current. */
  stack: string[];
  /** Values undone, oldest → newest; top is the next redo. */
  redo: string[];
};

type FieldUndoStore = {
  ensureBase: (path: string, value: string) => void;
  push: (path: string, previous: string) => void;
  pop: (path: string, current: string) => string | null;
  redo: (path: string, current: string) => string | null;
  peekCanUndo: (path: string) => boolean;
  peekCanRedo: (path: string) => boolean;
  depth: (path: string) => number;
  truncate: (path: string, size: number) => void;
  clearIfAtBase: (path: string, current: string) => void;
  version: number;
};

const FieldUndoCtx = React.createContext<FieldUndoStore | null>(null);

function useFieldUndoStore(): FieldUndoStore {
  const ctx = React.useContext(FieldUndoCtx);
  if (!ctx) {
    // Public / missing provider — no-op store
    return {
      ensureBase: () => {},
      push: () => {},
      pop: () => null,
      redo: () => null,
      peekCanUndo: () => false,
      peekCanRedo: () => false,
      depth: () => 0,
      truncate: () => {},
      clearIfAtBase: () => {},
      version: 0,
    };
  }
  return ctx;
}

function useFieldUndoStoreState(): FieldUndoStore {
  const mapRef = React.useRef<Map<string, FieldUndoEntry>>(new Map());
  const [version, setVersion] = React.useState(0);
  const tick = React.useCallback(() => setVersion((v) => v + 1), []);

  // Fresh map when provider remounts (new editor session).
  React.useEffect(() => {
    mapRef.current = new Map();
    tick();
  }, [tick]);

  // Stable API — do not recreate when `version` changes (that was resetting
  // in-flight edit state via effect deps and fighting contentEditable).
  const apiRef = React.useRef<Omit<FieldUndoStore, 'version'>>();
  if (!apiRef.current) {
    apiRef.current = {
      ensureBase: (path, value) => {
        if (!path || mapRef.current.has(path)) return;
        mapRef.current.set(path, { base: value, stack: [], redo: [] });
      },
      push: (path, previous) => {
        if (!path) return;
        const entry = mapRef.current.get(path) || { base: previous, stack: [], redo: [] };
        const clearedRedo = entry.redo.length > 0;
        entry.redo = [];
        const stacked = entry.stack[entry.stack.length - 1] !== previous;
        if (stacked) {
          entry.stack = [...entry.stack.slice(-79), previous];
        }
        if (stacked || clearedRedo) {
          mapRef.current.set(path, entry);
          tick();
        }
      },
      pop: (path, current) => {
        const entry = mapRef.current.get(path);
        if (!entry || entry.stack.length === 0) return null;
        const snapshot = entry.stack[entry.stack.length - 1];
        entry.stack = entry.stack.slice(0, -1);
        if (current !== snapshot) {
          entry.redo = [...entry.redo.slice(-79), current];
        }
        mapRef.current.set(path, entry);
        tick();
        return snapshot;
      },
      redo: (path, current) => {
        const entry = mapRef.current.get(path);
        if (!entry || entry.redo.length === 0) return null;
        const snapshot = entry.redo[entry.redo.length - 1];
        entry.redo = entry.redo.slice(0, -1);
        if (entry.stack[entry.stack.length - 1] !== current) {
          entry.stack = [...entry.stack.slice(-79), current];
        }
        mapRef.current.set(path, entry);
        tick();
        return snapshot;
      },
      peekCanUndo: (path) => {
        const entry = mapRef.current.get(path);
        return Boolean(entry && entry.stack.length > 0);
      },
      peekCanRedo: (path) => {
        const entry = mapRef.current.get(path);
        return Boolean(entry && entry.redo.length > 0);
      },
      depth: (path) => mapRef.current.get(path)?.stack.length ?? 0,
      truncate: (path, size) => {
        const entry = mapRef.current.get(path);
        if (!entry) return;
        const nextSize = Math.max(0, size);
        const stackSame = entry.stack.length === nextSize;
        const redoSame = entry.redo.length === 0;
        if (stackSame && redoSame) return;
        if (!stackSame) entry.stack = entry.stack.slice(0, nextSize);
        entry.redo = [];
        mapRef.current.set(path, entry);
        tick();
      },
      clearIfAtBase: (path, current) => {
        const entry = mapRef.current.get(path);
        if (!entry) return;
        if (current === entry.base) {
          entry.stack = [];
          entry.redo = [];
          mapRef.current.set(path, entry);
          tick();
        }
      },
    };
  }

  return React.useMemo<FieldUndoStore>(
    () => ({ ...apiRef.current!, version }),
    [version],
  );
}

/**
 * Persistent per-path text undo for inline fields.
 * History survives Enter/blur so re-entering edit mode can undo again,
 * word-by-word, back toward the base value from template open.
 * The undo chip itself only renders while the field is in edit mode.
 */
export function useFieldTextUndo(path: string, committedValue: string, editing: boolean) {
  const store = useFieldUndoStore();
  const lastRef = React.useRef(committedValue);
  const depthAtEditStartRef = React.useRef(0);
  const wasEditingRef = React.useRef(false);
  const [layoutTick, setLayoutTick] = React.useState(0);

  React.useEffect(() => {
    store.ensureBase(path, committedValue);
  }, [path, committedValue, store]);

  // Only sync from committed value when entering/leaving edit mode — never on
  // every store.version tick (that was wiping undo and restoring saved text).
  React.useEffect(() => {
    if (editing && !wasEditingRef.current) {
      depthAtEditStartRef.current = store.depth(path);
      lastRef.current = committedValue;
      setLayoutTick((n) => n + 1);
    } else if (!editing && wasEditingRef.current) {
      lastRef.current = committedValue;
    } else if (!editing) {
      lastRef.current = committedValue;
    }
    wasEditingRef.current = editing;
  }, [editing, committedValue, path, store]);

  const canUndo = store.peekCanUndo(path);
  const canRedo = store.peekCanRedo(path);

  const recordInput = React.useCallback(
    (next: string) => {
      const prev = lastRef.current;
      if (next === prev) return;

      const grew = next.length > prev.length;
      const added = grew ? next.slice(prev.length) : '';
      // Word-by-word checkpoints (Loom): push on first change, word boundary,
      // delete, or non-append replace — not on every mid-word character.
      const hitWordBoundary = grew && /[\s.,!?;:'"）\]}]/.test(added);
      const deleted = next.length < prev.length;
      const structural =
        !(next.startsWith(prev) || prev.startsWith(next));
      const firstChangeInSession =
        store.depth(path) === depthAtEditStartRef.current;

      if (firstChangeInSession || hitWordBoundary || deleted || structural) {
        store.push(path, prev); // version tick → chip can appear; CE must not re-render
      }

      lastRef.current = next;
      // Do not setLayoutTick on every keystroke — that remounts caret to start.
    },
    [path, store],
  );

  const onInput = React.useCallback(
    (el: HTMLElement, multiline: boolean) => {
      recordInput(readEditText(el, multiline));
    },
    [recordInput],
  );

  const undoOnce = React.useCallback(() => {
    // If the live text still has a trailing mid-word past the last checkpoint,
    // pop restores that checkpoint and keeps the live text for redo.
    const snapshot = store.pop(path, lastRef.current);
    if (snapshot == null) return null;
    lastRef.current = snapshot;
    setLayoutTick((n) => n + 1);
    return snapshot;
  }, [path, store]);

  const redoOnce = React.useCallback(() => {
    const snapshot = store.redo(path, lastRef.current);
    if (snapshot == null) return null;
    lastRef.current = snapshot;
    setLayoutTick((n) => n + 1);
    return snapshot;
  }, [path, store]);

  const markCommitted = React.useCallback(
    (current: string) => {
      lastRef.current = current;
      store.clearIfAtBase(path, current);
      setLayoutTick((n) => n + 1);
    },
    [path, store],
  );

  const cancelEditSession = React.useCallback(() => {
    // Drop only snapshots from this editing session (Escape).
    store.truncate(path, depthAtEditStartRef.current);
    lastRef.current = committedValue;
    setLayoutTick((n) => n + 1);
  }, [path, store, committedValue]);

  return {
    canUndo,
    canRedo,
    onInput,
    recordInput,
    undoOnce,
    redoOnce,
    markCommitted,
    cancelEditSession,
    layoutTick: layoutTick + store.version,
  };
}

/** ⌘Z / Ctrl+Z undo, ⌘⇧Z / Ctrl+Y redo. Null for every other key. */
export function inlineHistoryShortcut(
  e: { metaKey: boolean; ctrlKey: boolean; shiftKey: boolean; key: string },
): "undo" | "redo" | null {
  const key = e.key.toLowerCase();
  if ((e.metaKey || e.ctrlKey) && key === "z" && !e.shiftKey) return "undo";
  if (((e.metaKey || e.ctrlKey) && key === "z" && e.shiftKey) || (e.ctrlKey && key === "y")) {
    return "redo";
  }
  return null;
}

/** Floating undo / redo controls pinned to the bottom-right of the field / blue outline. */
export function EditSessionUndoChip({
  anchorRef,
  visible,
  canUndo,
  canRedo = false,
  onUndo,
  onRedo,
  layoutTick,
}: {
  anchorRef: React.RefObject<HTMLElement | null>;
  visible: boolean;
  canUndo: boolean;
  canRedo?: boolean;
  onUndo: () => void;
  onRedo?: () => void;
  layoutTick: number;
}) {
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);
  const show = visible && (canUndo || canRedo);

  React.useLayoutEffect(() => {
    if (!show) {
      setPos(null);
      return;
    }
    const update = () => {
      const el = anchorRef.current;
      if (!el) {
        setPos(null);
        return;
      }
      const r = el.getBoundingClientRect();
      const width = (canUndo ? 34 : 0) + (canRedo ? 34 : 0) + (canUndo && canRedo ? 6 : 0);
      // Bottom-right of the blue outline (outline-offset ~2px).
      setPos({
        top: Math.round(r.bottom + 6),
        left: Math.round(r.right - width),
      });
    };
    const node = anchorRef.current;
    if (!node) {
      setPos(null);
      return;
    }
    return subscribeAnchorReposition(node, update);
  }, [show, canUndo, canRedo, anchorRef, layoutTick]);

  if (!show || !pos) return null;

  return createPortal(
    <div
      className="om-edit-session-history"
      style={{ top: pos.top, left: Math.max(8, pos.left) }}
    >
      {canUndo ? (
        <button
          type="button"
          className="om-edit-session-undo"
          title="Undo text change"
          aria-label="Undo text change"
          onMouseDown={(e) => {
            // Keep focus in the field so blur does not commit mid-undo.
            e.preventDefault();
            e.stopPropagation();
            onUndo();
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M9.5 7.5L5 12l4.5 4.5M5.5 12H15a4 4 0 0 1 0 8h-2"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      ) : null}
      {canRedo && onRedo ? (
        <button
          type="button"
          className="om-edit-session-undo"
          title="Redo text change"
          aria-label="Redo text change"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRedo();
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M14.5 7.5L19 12l-4.5 4.5M18.5 12H9a4 4 0 0 0 0 8h2"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      ) : null}
    </div>,
    document.body,
  );
}

// ── Context ────────────────────────────────────────────────────────────────
export interface EditContextValue {
  get: (path: string, def: string) => string;
  set: (path: string, val: string | null) => void;
  /** Apply several paths in one `onChange` so later keys cannot clobber earlier ones. */
  setMany: (patch: Record<string, string | null>) => void;
  /** False on public `/website/:username` — inline fields render as static text/links. */
  editable: boolean;
  /**
   * When false, click-to-edit on the canvas is off (dashboard website inspector).
   * `set()` still works so the right-hand panel can update copy live.
   */
  inline: boolean;
}

export const EditCtx = React.createContext<EditContextValue>({
  get: (_p, d) => d,
  set: () => {},
  setMany: () => {},
  editable: true,
  inline: true,
});

export function useEdit(): EditContextValue {
  return React.useContext(EditCtx);
}

const EDIT_POP_EST_HEIGHT = 132;

/** Viewport + every overflow scroller between `el` and the document. */
function subscribeAnchorReposition(
  el: HTMLElement,
  onMove: () => void,
): () => void {
  onMove();
  const targets: EventTarget[] = [window];
  for (let n: HTMLElement | null = el.parentElement; n; n = n.parentElement) {
    const { overflow, overflowY, overflowX } = getComputedStyle(n);
    if (/(auto|scroll|overlay)/.test(`${overflow}${overflowY}${overflowX}`)) {
      targets.push(n);
    }
  }
  const opts: AddEventListenerOptions = { capture: true, passive: true };
  for (const t of targets) t.addEventListener('scroll', onMove, opts);
  window.addEventListener('resize', onMove);
  window.visualViewport?.addEventListener('scroll', onMove);
  window.visualViewport?.addEventListener('resize', onMove);
  const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onMove) : null;
  ro?.observe(el);
  return () => {
    for (const t of targets) t.removeEventListener('scroll', onMove, opts);
    window.removeEventListener('resize', onMove);
    window.visualViewport?.removeEventListener('scroll', onMove);
    window.visualViewport?.removeEventListener('resize', onMove);
    ro?.disconnect();
  };
}

function useEditPopoverStyle(
  anchorRef: React.RefObject<HTMLElement | null>,
  open: boolean,
) {
  const [style, setStyle] = React.useState<React.CSSProperties>({});

  React.useLayoutEffect(() => {
    if (!open) return;
    const el = anchorRef.current;
    if (!el) return;

    const update = () => {
      const node = anchorRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const gap = 6;
      const viewBottom = window.visualViewport?.height ?? window.innerHeight;
      const below = viewBottom - rect.bottom;
      const placeAbove =
        below < EDIT_POP_EST_HEIGHT + gap && rect.top > EDIT_POP_EST_HEIGHT + gap;
      const top = placeAbove
        ? rect.top - EDIT_POP_EST_HEIGHT - gap
        : rect.bottom + gap;
      // Follow the anchor even when it scrolls past the viewport — do not clamp
      // to 8px (that pinned Visit / Change link to the top of the page).
      setStyle({
        top,
        left: rect.left,
        minWidth: Math.max(160, rect.width),
      });
    };

    return subscribeAnchorReposition(el, update);
  }, [open, anchorRef]);

  return style;
}

function EditPopoverPortal({
  anchorRef,
  open,
  children,
}: {
  anchorRef: React.RefObject<HTMLElement | null>;
  open: boolean;
  children: React.ReactNode;
}) {
  const popStyle = useEditPopoverStyle(anchorRef, open);
  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="om-edit-pop om-edit-pop--portal"
      style={popStyle}
      role="menu"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body,
  );
}

// ── EditProvider ───────────────────────────────────────────────────────────
interface EditProviderProps {
  edits: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  children: React.ReactNode;
  /** When true (public website view), inline editing UI is disabled. */
  readOnly?: boolean;
  /** Dashboard website inspector: keep `set()` but disable click-to-edit on the canvas. */
  inline?: boolean;
}

export function EditProvider({
  edits,
  onChange,
  children,
  readOnly,
  inline = true,
}: EditProviderProps) {
  injectEditStyles();
  const editable = !readOnly;
  const fieldUndo = useFieldUndoStoreState();
  const editsRef = React.useRef(edits);
  editsRef.current = edits;
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  // Recreate context only when editability changes. `get` reads editsRef so
  // catalog tiles are not subscribed to every contentEdits keystroke.
  const value = React.useMemo<EditContextValue>(() => ({
    get: (path, def) => {
      if (!path) return def;
      const v = editsRef.current?.[path];
      return v == null ? def : v;
    },
    set: (path, val) => {
      if (!editable || !path) return;
      const next = { ...(editsRef.current || {}) };
      if (val == null) delete next[path];
      else next[path] = val;
      editsRef.current = next;
      onChangeRef.current(next);
    },
    setMany: (patch) => {
      if (!editable || !patch) return;
      const next = { ...(editsRef.current || {}) };
      let changed = false;
      for (const [path, val] of Object.entries(patch)) {
        if (!path) continue;
        if (val == null) {
          if (path in next) {
            delete next[path];
            changed = true;
          }
        } else if (next[path] !== val) {
          next[path] = val;
          changed = true;
        }
      }
      if (changed) {
        editsRef.current = next;
        onChangeRef.current(next);
      }
    },
    editable,
    inline: editable && inline,
  }), [editable, inline]);

  return (
    <FieldUndoCtx.Provider value={fieldUndo}>
      <EditCtx.Provider value={value}>{children}</EditCtx.Provider>
    </FieldUndoCtx.Provider>
  );
}

// ── E — editable text span ─────────────────────────────────────────────────
interface EProps extends React.HTMLAttributes<HTMLElement> {
  path: string;
  children?: React.ReactNode;
  as?: string;
  multiline?: boolean;
  /** Shown in the dashboard when the field was cleared (empty saved value). */
  placeholder?: string;
  /** Caps committed text. ContentEditable has no native maxlength. */
  maxLength?: number;
}

export function E({ path, children, as, multiline = false, placeholder = '', style, maxLength, ...rest }: EProps) {
  const tourRest =
    path === 'bio.name'
      ? { 'data-website-tour': 'inline-edit' as const, ...rest }
      : rest;
  const { get, set, editable, inline } = useEdit();
  const initial = typeof children === 'string' ? children : (children == null ? '' : String(children));
  const stored = get(path, initial);
  // Headline follows the live display name passed as children. A saved
  // content edit must not put the account name back on screen. Text committed
  // in this session stays visible until that display name changes.
  const [sessionName, setSessionName] = React.useState<string | null>(null);
  const sessionInitialRef = React.useRef(initial);
  if (path === "bio.name" && sessionInitialRef.current !== initial) {
    sessionInitialRef.current = initial;
    if (sessionName !== null) setSessionName(null);
  }
  // Hero title: prefer in-session edits, then contentEdits (including explicit
  // ""), then the live display name. Ignoring empty contentEdits used to put
  // the account name back and skip the required-title drawer.
  const rawValue =
    path === "bio.name"
      ? sessionName !== null
        ? sessionName
        : stored
      : isProductNameEditPath(path)
        ? cleanProductNameEditValue(stored, initial)
        : coerceRequiredLabel(path, stored, initial);
  // Never paint blank lines for Home Hero description (even if contentEdits
  // / live tagline still contain them).
  const value =
    path === "bio.tagline" ? sanitizeBioDescription(rawValue) : rawValue;
  const [editing, setEditing] = React.useState(false);
  // Live text while editing — ref only so typing does not re-render (caret jump).
  const draftRef = React.useRef(value);
  const ref = React.useRef<HTMLElement>(null);
  const Tag = ((as ?? (multiline ? 'div' : 'span')) as keyof JSX.IntrinsicElements);
  const fieldUndo = useFieldTextUndo(path, value, editing);
  // Track whether we already seeded the CE DOM for this edit session so parent
  // re-renders (live about fetch / inspector) never rewrite text + jump caret.
  const seededEditSessionRef = React.useRef(false);

  React.useLayoutEffect(() => {
    if (!editable) return;
    if (!editing) {
      seededEditSessionRef.current = false;
      return;
    }
    if (!ref.current || seededEditSessionRef.current) return;
    seededEditSessionRef.current = true;
    // Seed DOM once when entering edit mode. Preserve any caret the click
    // already placed — only force end when there is no selection inside.
    writeEditText(ref.current, draftRef.current, multiline);
    ref.current.focus();
    const sel = window.getSelection();
    const anchorInHost =
      sel &&
      sel.rangeCount > 0 &&
      ref.current.contains(sel.getRangeAt(0).commonAncestorContainer);
    if (!anchorInHost) {
      placeCaretAtEnd(ref.current);
    }
  }, [editing, editable, multiline]);

  const editStartRef = React.useRef(value);
  const finishClosedRef = React.useRef(false);
  const beginEditing = () => {
    if (!editable || !inline) return;
    draftRef.current = value;
    editStartRef.current = value;
    finishClosedRef.current = false;
    seededEditSessionRef.current = false;
    // About copy: pin the live string into contentEdits on first click so a late
    // DB about fetch cannot replace mid-edit (common on first About open).
    // Hero title: pin so clear → "" is a real contentEdits change (opens drawer).
    if (
      path === "about.bio" ||
      path === "about.heading" ||
      path.startsWith("about.story.") ||
      path === "bio.name"
    ) {
      set(path, value);
    }
    setEditing(true);
  };

  const finish = (commit = true) => {
    if (finishClosedRef.current) return;
    const live = ref.current
      ? readEditText(ref.current, multiline)
      : draftRef.current;
    // Remount wipe protection: restore draft only when the user never edited
    // away from the start value. An intentional clear sets draft to "" via
    // onInput — do not put the previous title back on blur.
    // Hero title: never restore — empty must reach handleContentEditsChange.
    let raw = live;
    const draftText = String(draftRef.current ?? "").trim();
    const startText = String(editStartRef.current ?? "").trim();
    if (
      commit &&
      path !== "bio.name" &&
      !String(live).trim() &&
      draftText &&
      draftText === startText
    ) {
      raw = draftRef.current;
    }
    draftRef.current = raw;
    finishClosedRef.current = true;
    seededEditSessionRef.current = false;
    if (commit) {
      // bio.tagline: collapse blank lines (same as LiB / EditBio). Other
      // multiline fields keep spaces-and-tabs-only trimming.
      let t =
        path === "bio.tagline"
          ? sanitizeBioDescription(raw)
          : !multiline
            ? raw.trim()
            : raw.replace(/^[ \t]+|[ \t]+$/g, "");
      if (maxLength != null && maxLength > 0 && t.length > maxLength) {
        t = t.slice(0, maxLength);
      }
      if (isProductNameEditPath(path)) {
        t = sanitizePublicCardTitle(t, { type: 'PRODUCT', product_name: t });
      }
      // Keep explicit empty strings so public site can hide cleared copy (vs. reverting to default),
      // except required Home labels which must never persist empty.
      if (!t && isRequiredNonEmptyLabelPath(path)) {
        t = coerceRequiredLabel(path, '', initial);
        draftRef.current = t;
        writeEditText(ref.current, t, multiline);
      }
      // Persist before leaving edit mode so Hero clear opens the Edit drawer
      // in the same turn (setEditing first could remount before onChange).
      // bio.tagline children = live fallback (`b.tagline`), not resolved
      // contentEdit — otherwise no-op blur clears Home copy back to LiB.
      // Compare against sanitized live so blank lines in b.tagline don't force
      // a redundant contentEdit.
      const initialForCommit =
        path === "bio.tagline" ? sanitizeBioDescription(initial) : initial;
      if (path === "bio.name") {
        set(path, t);
        setSessionName(t);
      } else if (t !== initialForCommit) {
        set(path, t);
      } else {
        set(path, null);
      }
      fieldUndo.markCommitted(t);
    } else {
      fieldUndo.cancelEditSession();
    }
    setEditing(false);
  };

  const applyUndo = () => {
    const snapshot = fieldUndo.undoOnce();
    if (snapshot == null) return;
    draftRef.current = snapshot;
    if (ref.current) {
      writeEditText(ref.current, snapshot, multiline);
      placeCaretAtEnd(ref.current);
    }
  };
  const applyRedo = () => {
    const snapshot = fieldUndo.redoOnce();
    if (snapshot == null) return;
    draftRef.current = snapshot;
    if (ref.current) {
      writeEditText(ref.current, snapshot, multiline);
      placeCaretAtEnd(ref.current);
    }
  };

  const canUndoRef = React.useRef(fieldUndo.canUndo);
  canUndoRef.current = fieldUndo.canUndo;
  const canRedoRef = React.useRef(fieldUndo.canRedo);
  canRedoRef.current = fieldUndo.canRedo;
  const applyUndoRef = React.useRef(applyUndo);
  applyUndoRef.current = applyUndo;
  const applyRedoRef = React.useRef(applyRedo);
  applyRedoRef.current = applyRedo;
  const finishRef = React.useRef(finish);
  finishRef.current = finish;
  const recordRef = React.useRef((next: string) => {
    draftRef.current = next;
    fieldUndo.recordInput(next);
  });
  recordRef.current = (next: string) => {
    let text = next;
    if (maxLength != null && maxLength > 0 && text.length > maxLength) {
      text = text.slice(0, maxLength);
      if (ref.current) {
        writeEditText(ref.current, text, multiline);
        placeCaretAtEnd(ref.current);
      }
    }
    draftRef.current = text;
    fieldUndo.recordInput(text);
  };

  if (!editable || !inline) {
    // Public / locked preview: omit empty cleared copy (no placeholder chrome).
    if (!String(value).trim() && !isRequiredNonEmptyLabelPath(path)) {
      return null;
    }
    return React.createElement(Tag as string, {
      style,
      ...(multiline ? { 'data-om-multiline': 'true' } : {}),
      ...tourRest,
    } as React.HTMLAttributes<HTMLElement>, value);
  }

  const isEmpty = !String(value).trim();
  const showUndoChip = editing && (fieldUndo.canUndo || fieldUndo.canRedo);
  const reactChildren = editing ? null : value;

  // Cleared copy stays off the canvas — restore via Edit drawer (matches EL / SchemaCanvasHtml).
  // Hero / required titles pass `placeholder` so the field stays clickable after clear
  // (empty h1/h2 shells + page-bodies overflow:clip were clipping the next section).
  if (
    isEmpty &&
    !editing &&
    !placeholder &&
    !isRequiredNonEmptyLabelPath(path) &&
    path !== "bio.name"
  ) {
    return null;
  }

  return (
    <>
      <OmEditableHost
        tag={Tag as string}
        editableHostRef={ref}
        editing={editing}
        reactChildren={reactChildren}
        placeholder={placeholder}
        isEmpty={isEmpty}
        style={style}
        path={path}
        rest={tourRest}
        multiline={multiline}
        onBeginEditing={beginEditing}
        canUndoRef={canUndoRef}
        canRedoRef={canRedoRef}
        applyUndoRef={applyUndoRef}
        applyRedoRef={applyRedoRef}
        finishRef={finishRef}
        recordRef={recordRef}
      />
      <EditSessionUndoChip
        anchorRef={ref}
        visible={showUndoChip}
        canUndo={fieldUndo.canUndo}
        canRedo={fieldUndo.canRedo}
        layoutTick={fieldUndo.layoutTick}
        onUndo={applyUndo}
        onRedo={applyRedo}
      />
    </>
  );
}

/**
 * Isolates contentEditable from parent re-renders (undo chip / store.version).
 * Re-renders only when edit mode toggles or idle label changes — never on
 * keystrokes / canUndo — so the caret does not jump after each word.
 */
const OmEditableHost = React.memo(function OmEditableHost({
  tag,
  editableHostRef,
  editing,
  reactChildren,
  placeholder,
  isEmpty,
  style,
  path,
  rest,
  multiline,
  onBeginEditing,
  canUndoRef,
  canRedoRef,
  applyUndoRef,
  applyRedoRef,
  finishRef,
  recordRef,
}: {
  tag: string;
  editableHostRef: React.RefObject<HTMLElement | null>;
  editing: boolean;
  reactChildren: string | null;
  placeholder: string;
  isEmpty: boolean;
  path: string;
  style?: React.CSSProperties;
  rest: React.HTMLAttributes<HTMLElement>;
  multiline: boolean;
  onBeginEditing: () => void;
  canUndoRef: React.MutableRefObject<boolean>;
  canRedoRef: React.MutableRefObject<boolean>;
  applyUndoRef: React.MutableRefObject<() => void>;
  applyRedoRef: React.MutableRefObject<() => void>;
  finishRef: React.MutableRefObject<(commit: boolean) => void>;
  recordRef: React.MutableRefObject<(next: string) => void>;
}) {
  return React.createElement(tag, {
    ref: editableHostRef,
    'data-om-edit': true,
    'data-om-edit-empty': isEmpty ? 'true' : 'false',
    'data-placeholder': placeholder,
    'data-editing': editing ? 'true' : 'false',
    ...(multiline ? { 'data-om-multiline': 'true' } : {}),
    contentEditable: editing,
    suppressContentEditableWarning: true,
    onClick: (e: React.MouseEvent) => {
      if (!editing) { e.stopPropagation(); e.preventDefault(); onBeginEditing(); }
    },
    onInput: () => {
      if (!editing || !editableHostRef.current) return;
      const text = readEditText(editableHostRef.current, multiline);
      editableHostRef.current.setAttribute(
        'data-om-edit-empty',
        String(text).trim() ? 'false' : 'true',
      );
      recordRef.current(text);
    },
    onBlur: () => editing && finishRef.current(true),
    onKeyDown: (e: React.KeyboardEvent) => {
      if (!editing) return;
      const historyKey = inlineHistoryShortcut(e);
      if (historyKey === 'undo') {
        if (canUndoRef.current) {
          e.preventDefault();
          e.stopPropagation();
          applyUndoRef.current();
        }
        return;
      }
      if (historyKey === 'redo') {
        if (canRedoRef.current) {
          e.preventDefault();
          e.stopPropagation();
          applyRedoRef.current();
        }
        return;
      }
      if (e.key === 'Enter') {
        if (!multiline) {
          e.preventDefault();
          // Empty Hero title: finish persists "" and opens the Edit drawer
          // (handleContentEditsChange) — no separate toast here.
          finishRef.current(true);
          (editableHostRef.current as HTMLElement | null)?.blur();
          return;
        }
        // Multiline descriptions: soft break, not commit / not block <div>.
        e.preventDefault();
        insertSoftLineBreak();
        if (editableHostRef.current) {
          recordRef.current(readEditText(editableHostRef.current, multiline));
        }
        return;
      }
      if (e.key === 'Escape') { e.preventDefault(); finishRef.current(false); }
      if (e.key === 'Tab') {
        e.preventDefault();
        finishRef.current(true);
        (editableHostRef.current as HTMLElement | null)?.blur();
      }
    },
    style,
    ...rest,
    'data-om-path': path,
  } as React.HTMLAttributes<HTMLElement>, reactChildren);
}, (prev, next) => (
  prev.editing === next.editing
  && prev.reactChildren === next.reactChildren
  // While editing, ignore isEmpty flips from live-data / sibling inspector updates —
  // remounting a contentEditable host mid-type wipes caret + text.
  && (prev.editing || prev.isEmpty === next.isEmpty)
  && prev.placeholder === next.placeholder
  && prev.multiline === next.multiline
  && prev.style === next.style
  && prev.tag === next.tag
));

// ── EL — editable hyperlinked text ────────────────────────────────────────
interface ELProps extends React.HTMLAttributes<HTMLElement> {
  path: string;
  href?: string;
  children?: React.ReactNode;
  as?: string;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
  /**
   * When true, the label always matches `children` (e.g. the real platform name).
   * Users may still change the URL via the link menu — avoids mislabeled social destinations.
   */
  lockText?: boolean;
  /**
   * When set with `onClick`, a normal click runs navigation/action; double-click opens the edit menu.
   * Use for in-preview nav links (Search, tabs) so one click changes page.
   */
  navigateOnClick?: boolean;
  /**
   * With `navigateOnClick`, wait this long before firing `onClick` so a quick second click can
   * open the edit menu instead (mobile nav drawer).
   */
  deferNavigateMs?: number;
  /** Hide “Change link” — e.g. shop category labels that only Visit / Change text. */
  omitChangeLink?: boolean;
}

export function EL({ path, href, children, as = 'a', style, onClick, lockText, navigateOnClick, deferNavigateMs, omitChangeLink, ...rest }: ELProps) {
  const { get, set, editable, inline } = useEdit();
  const initialText = typeof children === 'string' ? children : (children == null ? '' : String(children));
  const navKey =
    path.startsWith('nav.') && !path.slice(4).includes('.') ? path.slice(4) : '';
  const rawText = lockText
    ? initialText
    : (() => {
        const fromText = get(`${path}.text`, '');
        if (fromText !== '') return fromText;
        if (navKey) {
          const fromBare = get(path, '');
          if (fromBare !== '') return fromBare;
        }
        return initialText;
      })();
  const text = lockText
    ? initialText
    : coerceRequiredLabel(path, rawText, initialText);
  const url = (() => {
    const fromHref = get(`${path}.href`, '');
    if (fromHref !== '') return fromHref;
    if (navKey) {
      const fromSlug = get(`nav.slug.${navKey}`, '');
      if (fromSlug !== '') return fromSlug;
    }
    return href || '#';
  })();
  const [menu, setMenu] = React.useState<null | 'menu' | 'text' | 'link'>(null);
  const ref = React.useRef<HTMLElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const pendingNavRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const Tag = as as keyof JSX.IntrinsicElements;
  const textEditing = menu === 'text';
  const textPath = `${path}.text`;
  const fieldUndo = useFieldTextUndo(textPath, text, textEditing);
  const textDraftRef = React.useRef(text);

  React.useLayoutEffect(() => {
    if (textEditing && ref.current) {
      textDraftRef.current = text;
      if (ref.current.innerText !== text) {
        ref.current.innerText = text;
      }
    }
  }, [textEditing]); // seed once when entering text edit

  const clearPendingNav = React.useCallback(() => {
    if (pendingNavRef.current) {
      clearTimeout(pendingNavRef.current);
      pendingNavRef.current = null;
    }
  }, []);

  React.useEffect(() => () => clearPendingNav(), [clearPendingNav]);

  React.useEffect(() => {
    if (menu === 'text' && ref.current) {
      ref.current.focus();
      const r = document.createRange();
      r.selectNodeContents(ref.current);
      r.collapse(false);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(r);
    }
    if (menu === 'link' && inputRef.current) inputRef.current.focus();
  }, [menu]);

  React.useEffect(() => {
    if (!menu || menu === 'text') return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Element;
      if (t.closest('.om-edit-pop')) return;
      if (ref.current?.contains(t)) return;
      setMenu(null);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menu]);

  const className = String((rest as { className?: string }).className ?? '');
  /** Template nav labels use in-preview routing — no custom href editing in the editor. */
  const isTemplateNavPath = path.startsWith('nav.');
  /** Footer site pages keep Change link, but Visit should route like header nav. */
  const isFooterNavPath = path.startsWith('footer.navLabel.');
  const isButtonLink =
    navigateOnClick || className.includes('mte-tpl-btn');
  const linkable = isLinkableTemplateHref(url);
  /** Dashboard: one click opens the edit menu; `navigateOnClick` is public-site behaviour only. */
  const useNavigateOnClick = Boolean(navigateOnClick) && (!editable || !inline);

  const finishText = (commit = true) => {
    if (lockText) {
      setMenu(null);
      return;
    }
    const live = ref.current
      ? readEditText(ref.current, false)
      : textDraftRef.current;
    textDraftRef.current = live;
    if (commit) {
      let t = live.trim();
      // Home is the required base menu — restore default instead of saving empty.
      if (!t && isRequiredNonEmptyLabelPath(path)) {
        t = coerceRequiredLabel(path, '', initialText);
        textDraftRef.current = t;
        if (ref.current) ref.current.innerText = t;
      }
      // Nav page labels share the 20-char header budget (not the wordmark).
      if (isTemplateNavPath && path !== 'nav.name') {
        t = t.slice(0, 20);
        textDraftRef.current = t;
        if (ref.current) ref.current.innerText = t;
        const stored = get(`${path}.text`, '');
        // Keystrokes already saved this string. Equality with `initialText`
        // must not clear it — the children prop updates from that save.
        if (!t) {
          if (!isRequiredNonEmptyLabelPath(path)) set(`${path}.text`, null);
        } else if (stored !== t) {
          if (t !== initialText || stored) set(`${path}.text`, t);
          else set(`${path}.text`, null);
        }
      } else if (t !== initialText) set(`${path}.text`, t);
      else set(`${path}.text`, null);
      fieldUndo.markCommitted(t);
    } else {
      fieldUndo.cancelEditSession();
    }
    setMenu(null);
  };

  const applyTextUndo = () => {
    const snapshot = fieldUndo.undoOnce();
    if (snapshot == null) return;
    textDraftRef.current = snapshot;
    if (ref.current) {
      ref.current.innerText = snapshot;
      placeCaretAtEnd(ref.current);
    }
  };
  const applyTextRedo = () => {
    const snapshot = fieldUndo.redoOnce();
    if (snapshot == null) return;
    textDraftRef.current = snapshot;
    if (ref.current) {
      ref.current.innerText = snapshot;
      placeCaretAtEnd(ref.current);
    }
  };
  const saveLink = (v: string) => {
    set(`${path}.href`, (v && v !== href) ? v : null);
    setMenu(null);
  };

  if (!editable || !inline) {
    // Public / locked preview: omit empty cleared link labels.
    if (!String(text).trim() && !isRequiredNonEmptyLabelPath(path) && !lockText) {
      return null;
    }
    const Tag = as as keyof JSX.IntrinsicElements;
    return React.createElement(Tag as string, {
      href: url,
      target: isButtonLink && linkable ? '_blank' : undefined,
      rel: isButtonLink && linkable ? 'noopener noreferrer' : undefined,
      style,
      onClick: (e: React.MouseEvent<HTMLElement>) => {
        if (onClick) {
          onClick(e);
        }
        // In-app nav already called preventDefault + SPA route — don't open raw
        // hrefs like `/blog` (those hit `/:username` and leave the website).
        if (navigateOnClick && e.defaultPrevented) {
          return;
        }
        if (isButtonLink && linkable) {
          e.preventDefault();
          openTemplateButtonHref(url);
        }
      },
      ...rest,
    } as React.AnchorHTMLAttributes<HTMLAnchorElement>, text);
  }

  const isEmptyLabel = !String(text).trim();
  // Loom: cleared CTA / footer labels leave the canvas; restore via Edit drawer.
  if (
    isEmptyLabel &&
    menu !== 'text' &&
    !isRequiredNonEmptyLabelPath(path) &&
    !lockText
  ) {
    return null;
  }

  const anchor = (
    React.createElement(Tag as string, {
    ref,
    href: url,
    'data-om-edit-link': true,
    'data-om-edit': true,
    'data-om-path': path,
    'data-om-edit-empty':
      editable && isEmptyLabel && menu !== 'text' && !isButtonLink
        ? 'true'
        : 'false',
    'data-editing': menu === 'text' ? 'true' : 'false',
    contentEditable: menu === 'text',
    suppressContentEditableWarning: true,
    target: isButtonLink && linkable ? '_blank' : undefined,
    rel: isButtonLink && linkable ? 'noopener noreferrer' : undefined,
    onClick: (e: React.MouseEvent<HTMLElement>) => {
      if (menu === 'text') return;
      // In the editor, only explicit `navigateOnClick` links navigate on single click.
      // Styled CTAs (e.g. banner buttons) open the edit menu instead of following the URL.
      if (useNavigateOnClick) {
        if (e.detail >= 2) {
          clearPendingNav();
          e.preventDefault();
          e.stopPropagation();
          setMenu((m) => (m ? null : 'menu'));
          return;
        }
        if (onClick && deferNavigateMs && deferNavigateMs > 0) {
          clearPendingNav();
          e.preventDefault();
          e.stopPropagation();
          const ev = e;
          pendingNavRef.current = setTimeout(() => {
            pendingNavRef.current = null;
            onClick(ev);
          }, deferNavigateMs);
          return;
        }
        if (onClick) {
          e.preventDefault();
          onClick(e);
          return;
        }
        if (openTemplateButtonHref(url)) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }
      e.preventDefault();
      e.stopPropagation();
      setMenu((m) => m ? null : 'menu');
    },
    onDoubleClick:
      useNavigateOnClick || isButtonLink
        ? (e: React.MouseEvent) => {
            clearPendingNav();
            e.preventDefault();
            e.stopPropagation();
            setMenu((m) => (m ? null : 'menu'));
          }
        : undefined,
    onInput: () => {
      if (menu !== 'text' || !ref.current) return;
      const next = readEditText(ref.current, false);
      textDraftRef.current = next;
      fieldUndo.recordInput(next);
      // Keep the Pages rail and nav inspector in step while the label is typed.
      if (isTemplateNavPath && path !== 'nav.name') {
        const t = next.trim().slice(0, 20);
        if (t && t !== initialText) set(`${path}.text`, t);
      }
    },
    onBlur: () => menu === 'text' && finishText(true),
    onKeyDown: (e: React.KeyboardEvent) => {
      if (menu !== 'text') return;
      const historyKey = inlineHistoryShortcut(e);
      if (historyKey === 'undo') {
        if (fieldUndo.canUndo) {
          e.preventDefault();
          e.stopPropagation();
          applyTextUndo();
        }
        return;
      }
      if (historyKey === 'redo') {
        if (fieldUndo.canRedo) {
          e.preventDefault();
          e.stopPropagation();
          applyTextRedo();
        }
        return;
      }
      if (e.key === 'Enter') { e.preventDefault(); finishText(true); (ref.current as HTMLElement | null)?.blur(); }
      if (e.key === 'Escape') { e.preventDefault(); finishText(false); }
      if (e.key === 'Tab') { e.preventDefault(); finishText(true); (ref.current as HTMLElement | null)?.blur(); }
    },
    style,
    ...rest,
  } as React.AnchorHTMLAttributes<HTMLAnchorElement>,
    // Null while text-editing so React does not reset caret on chip re-renders.
    textEditing ? null : text,
  ));

  return (
    <>
      {anchor}
      <EditSessionUndoChip
        anchorRef={ref}
        visible={textEditing && (fieldUndo.canUndo || fieldUndo.canRedo)}
        canUndo={fieldUndo.canUndo}
        canRedo={fieldUndo.canRedo}
        layoutTick={fieldUndo.layoutTick}
        onUndo={applyTextUndo}
        onRedo={applyTextRedo}
      />
      <EditPopoverPortal anchorRef={ref} open={menu === 'menu'}>
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            setMenu(null);
            clearPendingNav();
            if (isTemplateNavPath || isFooterNavPath) {
              const customHref = isFooterNavPath
                ? get(`${path}.href`, '').trim()
                : '';
              if (customHref && openTemplateButtonHref(customHref)) return;
              if (onClick) {
                (onClick as React.MouseEventHandler<HTMLElement>)(e as unknown as React.MouseEvent<HTMLElement>);
              }
              return;
            }
            if (openTemplateButtonHref(url)) return;
            if (onClick) {
              (onClick as React.MouseEventHandler<HTMLElement>)(e as unknown as React.MouseEvent<HTMLElement>);
            }
          }}
        >
          ↗ Visit link
        </button>
        {!lockText && (
          <button type="button" onMouseDown={(e) => { e.preventDefault(); setMenu('text'); }}>
            ✎  {isTemplateNavPath ? 'Edit text' : 'Change text'}
          </button>
        )}
        {!isTemplateNavPath && !omitChangeLink && (
          <button type="button" onMouseDown={(e) => { e.preventDefault(); setMenu('link'); }}>🔗  Change link</button>
        )}
      </EditPopoverPortal>
      <EditPopoverPortal anchorRef={ref} open={menu === 'link'}>
        <input
          ref={inputRef}
          type="url"
          defaultValue={url}
          placeholder="https://…"
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); saveLink(e.currentTarget.value); }
            if (e.key === 'Escape') setMenu(null);
          }}
        />
        <div className="om-row">
          <button
            type="button"
            className="mte-inline-pop-save"
            onMouseDown={(e) => { e.preventDefault(); saveLink(inputRef.current?.value || ''); }}
          >Save</button>
          <button
            type="button"
            className="mte-inline-pop-cancel"
            onMouseDown={(e) => { e.preventDefault(); setMenu(null); }}
          >Cancel</button>
        </div>
      </EditPopoverPortal>
    </>
  );
}
